import { useState } from 'react';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import {
  useCreateFeedbackObjection,
  useJudgeFeedbackEntry,
  useWithdrawFeedbackObjection,
} from '../../../hooks/useProjectFeedback.js';
import { ApiError } from '../../../lib/api.js';
import { formatLocalYmd } from '../../../lib/date-utils.js';
import {
  hasSkillName,
  referenceHref,
  skillMapLabel,
  skillMapTitle,
  verdictLabel,
} from '../../../lib/project-feedback-view.js';
import type {
  FeedbackEntry,
  FeedbackJudgment,
  FeedbackObjection,
} from '../../../types/project-feedback.js';

/** @graph-connects none */
export interface FeedbackEntryItemProps {
  /** FB の ID */
  feedbackId: string;
  /** 表示している版 */
  version: number;
  /** 項目 */
  entry: FeedbackEntry;
  /** この版・項目の判断 */
  judgment: FeedbackJudgment | undefined;
  /** この版・項目の異議 */
  objections: FeedbackObjection[];
  /** API が返した、自分にできること */
  permissions: { canObject: boolean; canJudge: boolean };
  /** API が返した判断の選択肢 */
  verdictOptions: string[];
  /** メールから表示名を引く */
  nameOf: (email: string) => string;
}

/**
 * API の文面をそのまま見せる（入口に判定を写さない）。
 * @graph-connects ApiError [references] API エラーの文面
 */
function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * 根拠の一覧。開ける形のキーはリンクにする。
 * @graph-connects referenceHref [calls] 根拠のリンク先
 */
function ReferenceList({ references }: { references: FeedbackEntry['references'] }) {
  return (
    <ul className="space-y-1">
      {references.map((reference, index) => {
        const href = referenceHref(reference);
        return (
          <li
            key={`${reference.key}-${String(index)}`}
            className="text-xs text-[var(--color-text-muted)] break-words"
          >
            <span className="font-mono">[{reference.type}]</span>{' '}
            {href === null ? (
              <span className="font-mono">{reference.key}</span>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[var(--color-accent-primary)] hover:underline"
              >
                {reference.key}
              </a>
            )}{' '}
            {reference.detail}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 評価者の判断の表示（本人にも見える）。
 * @graph-connects verdictLabel [calls] 判断の表示名
 */
function JudgmentView({
  judgment,
  nameOf,
}: {
  judgment: FeedbackJudgment;
  nameOf: (email: string) => string;
}) {
  return (
    <div className="rounded-md bg-[var(--color-bg-surface)] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={judgment.verdict === 'valid' ? 'success' : 'default'}>
          {verdictLabel(judgment.verdict)}
        </Badge>
        <span className="text-xs text-[var(--color-text-muted)]">
          {nameOf(judgment.by)} · {formatLocalYmd(judgment.at)}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-[var(--color-text-primary)]">{judgment.reason}</p>
    </div>
  );
}

/**
 * 判断を付ける・付け直すフォーム。選択肢は API の verdictOptions。
 * @graph-connects useJudgeFeedbackEntry [calls] 判断の保存
 * @graph-connects useToast [calls] 失敗の通知
 */
function JudgmentForm({
  feedbackId,
  version,
  entryId,
  current,
  verdictOptions,
  onDone,
}: {
  feedbackId: string;
  version: number;
  entryId: string;
  current: FeedbackJudgment | undefined;
  verdictOptions: string[];
  onDone: () => void;
}) {
  const [verdict, setVerdict] = useState(current?.verdict ?? '');
  const [reason, setReason] = useState(current?.reason ?? '');
  const judge = useJudgeFeedbackEntry(feedbackId);
  const { showToast } = useToast();
  const canSubmit = verdict !== '' && reason.trim() !== '' && !judge.isPending;

  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        judge.mutate(
          { entryId, version, verdict, reason: reason.trim() },
          {
            onSuccess: onDone,
            onError: (error) => {
              showToast('error', messageOf(error, '判断の保存に失敗しました'));
            },
          },
        );
      }}
    >
      <fieldset className="flex flex-wrap gap-4">
        <legend className="sr-only">判断</legend>
        {verdictOptions.map((option) => (
          <label key={option} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              name={`verdict-${entryId}`}
              value={option}
              checked={verdict === option}
              onChange={() => {
                setVerdict(option);
              }}
              className="accent-[var(--color-accent-primary)]"
            />
            {verdictLabel(option)}
          </label>
        ))}
      </fieldset>
      <Textarea
        aria-label="判断の理由"
        placeholder="理由"
        rows={2}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          キャンセル
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
          保存
        </Button>
      </div>
    </form>
  );
}

/**
 * 異議を出すフォーム。
 * @graph-connects useCreateFeedbackObjection [calls] 異議の作成
 * @graph-connects useToast [calls] 失敗の通知
 */
function ObjectionForm({
  feedbackId,
  version,
  entryId,
  onDone,
}: {
  feedbackId: string;
  version: number;
  entryId: string;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const create = useCreateFeedbackObjection(feedbackId);
  const { showToast } = useToast();

  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          { entryId, version, reason: reason.trim() },
          {
            onSuccess: onDone,
            onError: (error) => {
              showToast('error', messageOf(error, '異議の送信に失敗しました'));
            },
          },
        );
      }}
    >
      <Textarea
        aria-label="異議の理由"
        placeholder="異議の理由"
        rows={2}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          キャンセル
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={reason.trim() === '' || create.isPending}
        >
          異議を出す
        </Button>
      </div>
    </form>
  );
}

/**
 * 異議の一覧。本人は出している異議を取り下げられる。
 * @graph-connects useWithdrawFeedbackObjection [calls] 異議の取り下げ
 * @graph-connects useToast [calls] 失敗の通知
 */
function ObjectionList({
  feedbackId,
  objections,
  canWithdraw,
  nameOf,
}: {
  feedbackId: string;
  objections: FeedbackObjection[];
  canWithdraw: boolean;
  nameOf: (email: string) => string;
}) {
  const withdraw = useWithdrawFeedbackObjection(feedbackId);
  const { showToast } = useToast();
  return (
    <ul className="space-y-2">
      {objections.map((objection) => {
        const isOpen = objection.status === 'open';
        return (
          <li
            key={objection.objectionId}
            className={`rounded-md border border-[var(--color-border)] p-3 text-sm ${
              isOpen ? '' : 'opacity-60'
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-[var(--color-text-muted)]">
                異議 · {nameOf(objection.by)} · {formatLocalYmd(objection.at)}
                {!isOpen && ' · 取り下げ済み'}
              </span>
              {isOpen && canWithdraw && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={withdraw.isPending}
                  onClick={() => {
                    withdraw.mutate(objection.objectionId, {
                      onError: (error) => {
                        showToast('error', messageOf(error, '異議の取り下げに失敗しました'));
                      },
                    });
                  }}
                >
                  取り下げる
                </Button>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-[var(--color-text-primary)]">
              {objection.reason}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * good / more の 1 項目。本文・根拠・判断・異議と、権限に応じた操作を並べる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Render one good-or-more feedback entry with its references, the evaluator verdict, owner objections, and the objection or judgment actions the API allows
 * @graph-connects ReferenceList [calls] 根拠の表示
 * @graph-connects JudgmentView [calls] 判断の表示
 * @graph-connects JudgmentForm [calls] 判断の入力
 * @graph-connects ObjectionForm [calls] 異議の入力
 * @graph-connects ObjectionList [calls] 異議の表示・取り下げ
 */
export function FeedbackEntryItem({
  feedbackId,
  version,
  entry,
  judgment,
  objections,
  permissions,
  verdictOptions,
  nameOf,
}: FeedbackEntryItemProps) {
  const [mode, setMode] = useState<'none' | 'object' | 'judge'>('none');
  const close = () => {
    setMode('none');
  };

  return (
    <li className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
      <div className="space-y-1">
        <p
          title={skillMapTitle(entry)}
          className={`text-xs font-medium text-[var(--color-text-secondary)]${hasSkillName(entry) ? '' : ' font-mono'}`}
        >
          {skillMapLabel(entry)}
        </p>
        <p className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">{entry.text}</p>
      </div>
      <ReferenceList references={entry.references} />
      {judgment !== undefined && mode !== 'judge' && (
        <JudgmentView judgment={judgment} nameOf={nameOf} />
      )}
      {objections.length > 0 && (
        <ObjectionList
          feedbackId={feedbackId}
          objections={objections}
          canWithdraw={permissions.canObject}
          nameOf={nameOf}
        />
      )}
      {mode === 'judge' && (
        <JudgmentForm
          feedbackId={feedbackId}
          version={version}
          entryId={entry.entryId}
          current={judgment}
          verdictOptions={verdictOptions}
          onDone={close}
        />
      )}
      {mode === 'object' && (
        <ObjectionForm
          feedbackId={feedbackId}
          version={version}
          entryId={entry.entryId}
          onDone={close}
        />
      )}
      {mode === 'none' && (permissions.canObject || permissions.canJudge) && (
        <div className="flex justify-end gap-2">
          {permissions.canObject && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode('object');
              }}
            >
              異議を出す
            </Button>
          )}
          {permissions.canJudge && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setMode('judge');
              }}
            >
              {judgment === undefined ? '判断する' : '判断を付け直す'}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
